// RotatingBoard — paginates the selected doctors into fixed-size pages and
// rotates through them on a timer with a soft fade.
//
//   • Slideshow → pageSize 1 (one doctor per screen, larger panel)
//   • Column    → pageSize 2 (two doctors side-by-side, rotates if more)
//
// With one page it's static (no rotation, no arrows, no dots). When there are
// multiple pages, left/right arrows fade in on hover for manual navigation;
// using them restarts the auto-rotate timer.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Doctor } from "../../lib/utils/types";
import { BookingV2, DocDelay, SessionMeta } from "../types";
import DoctorQueuePanel, { Density } from "./DoctorQueuePanel";

interface Props {
  doctors: Doctor[];
  bookingsByMapping: Record<string, BookingV2[]>;
  delaysByAvailability: Record<string, DocDelay>;
  sessionsByAvailability: Record<string, SessionMeta>;
  pageSize: number;
  intervalSec: number;
  variant: "column" | "slide";
  // Set when the ads panel is sharing the screen. A solo doctor is normally
  // inset to 75% so it doesn't look stretched, but next to a 50% ads panel that
  // inset reads as the board being the smaller half — so fill it instead.
  fillWidth?: boolean;
}

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += Math.max(1, size)) {
    out.push(arr.slice(i, i + Math.max(1, size)));
  }
  return out;
};

const Chevron: React.FC<{ dir: "left" | "right" }> = ({ dir }) => (
  <svg viewBox="0 0 24 24" fill="none" className="h-[0.875rem] w-[0.875rem]">
    <path
      d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
      stroke="#1F2B4D"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RotatingBoard: React.FC<Props> = ({
  doctors,
  bookingsByMapping,
  delaysByAvailability,
  sessionsByAvailability,
  pageSize,
  intervalSec,
  variant,
  fillWidth = false,
}) => {
  const pages = useMemo(() => chunk(doctors, pageSize), [doctors, pageSize]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  // Bumped on manual navigation to restart the auto-rotate timer.
  const [interaction, setInteraction] = useState(0);

  // Keep index valid if the page count shrinks.
  useEffect(() => {
    if (index >= pages.length) setIndex(0);
  }, [pages.length, index]);

  const indexRef = useRef(index);
  indexRef.current = index;
  const pageCountRef = useRef(pages.length);
  pageCountRef.current = pages.length;

  // Fade out, swap to target page, fade back in.
  const goTo = useCallback((resolve: (cur: number, count: number) => number) => {
    const count = pageCountRef.current;
    if (count <= 1) return;
    setVisible(false);
    setTimeout(() => {
      setIndex((cur) => ((resolve(cur, count) % count) + count) % count);
      setVisible(true);
    }, 350);
  }, []);

  const next = useCallback(() => goTo((cur) => cur + 1), [goTo]);
  const prev = useCallback(() => goTo((cur) => cur - 1), [goTo]);

  const manualNext = useCallback(() => {
    next();
    setInteraction((n) => n + 1);
  }, [next]);
  const manualPrev = useCallback(() => {
    prev();
    setInteraction((n) => n + 1);
  }, [prev]);

  // Auto-rotate. Restarts whenever the user navigates manually (interaction).
  useEffect(() => {
    if (pages.length <= 1) return;
    const id = setInterval(next, Math.max(3, intervalSec) * 1000);
    return () => clearInterval(id);
  }, [pages.length, intervalSec, interaction, next]);

  const current = pages[index] ?? [];
  // A page showing one doctor is inset to 75% of the board and centred. Two
  // paths land here: the slideshow layout (pageSize 1), and the column layout
  // once patient-less doctors are filtered out. Full-bleed looks stretched and
  // the slideshow's old max-w-3xl cap looked lost on a large screen. The inset
  // is dropped when the ads panel is up (fillWidth), where it would otherwise
  // shrink the board to 37.5% of the screen against a 50% ads panel.
  const solo = current.length === 1;
  const inset = solo && !fillWidth;
  const cols = Math.max(1, Math.min(current.length, pageSize));
  const multi = pages.length > 1;

  // Row density. Slideshow panels are large. Two column panels next to the
  // 50% ads panel each get a quarter of the screen — at normal density the
  // token + status pills alone filled a row and names were cut to a few
  // letters, so those drop to compact. Widths are deterministic here because
  // the board scales to the viewport (useTVScale), so this needn't be measured.
  const density: Density =
    variant === "slide" ? "large" : fillWidth && cols > 1 ? "compact" : "normal";

  // Faint by default (a small highlight so it's findable), full on hover.
  // Deliberately small: the controls are a reference, not part of the board.
  const arrowBtn =
    "flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-[0_1px_6px_rgba(16,24,40,0.12)] ring-1 ring-[#E9EDF3] opacity-30 transition-opacity duration-200 hover:opacity-100";

  // The bottom row is also reserved (empty) next to the ads panel, whose own
  // bottom row holds the fullscreen button: both columns then give the same
  // height to their content and the doctor panels end level with the ad.
  const controlsRow = multi || fillWidth;

  return (
    <div className="flex h-full flex-col">
      <div
        className={`min-h-0 flex-1 transition-opacity duration-300 ${
          inset ? "flex justify-center" : ""
        }`}
        style={{ opacity: visible ? 1 : 0 }}
      >
        <div
          className={`grid h-full gap-6 ${inset ? "w-3/4" : "w-full"}`}
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {current.map((doc) => (
            <DoctorQueuePanel
              key={doc.mapping_id}
              doctor={doc}
              bookings={bookingsByMapping[doc.mapping_id] ?? []}
              delaysByAvailability={delaysByAvailability}
              sessionsByAvailability={sessionsByAvailability}
              density={density}
            />
          ))}
        </div>
      </div>

      {/* Bottom controls: faint arrows + page dots, centered */}
      {controlsRow && (
        <div className="mt-3 flex h-6 shrink-0 items-center justify-center gap-3">
          {multi && (
            <>
              <button aria-label="Previous" onClick={manualPrev} className={arrowBtn}>
                <Chevron dir="left" />
              </button>

              <div className="flex items-center gap-1.5">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to page ${i + 1}`}
                    onClick={() => {
                      goTo(() => i);
                      setInteraction((n) => n + 1);
                    }}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === index ? "1.125rem" : "0.375rem",
                      backgroundColor: i === index ? "#0B74FF" : "#D7DEE9",
                    }}
                  />
                ))}
              </div>

              <button aria-label="Next" onClick={manualNext} className={arrowBtn}>
                <Chevron dir="right" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RotatingBoard;
